package com.evoapps.evoshift.observability

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * The person's choice on this device (SH-86). Both off until they allow them; [asked]: the
 * first-run question was answered (either way), so it is not shown again.
 */
data class TelemetryConsent(
    val crashReports: Boolean = false,
    val usageStats: Boolean = false,
    val asked: Boolean = false
)

/** Where the choice is kept on this device (SharedPreferences, NSUserDefaults, localStorage). */
interface ConsentStore {
    fun load(): TelemetryConsent
    fun save(consent: TelemetryConsent)
}

class InMemoryConsentStore(private var consent: TelemetryConsent = TelemetryConsent()) : ConsentStore {
    override fun load(): TelemetryConsent = consent
    override fun save(consent: TelemetryConsent) {
        this.consent = consent
    }
}

expect fun platformConsentStore(): ConsentStore

/**
 * The consent gate's state: loads the stored choice and applies it to [telemetry] at once (before
 * that, nothing collects), and applies every change immediately, withdrawal included.
 */
class PrivacyConsent(
    private val store: ConsentStore,
    private val telemetry: Telemetry,
    val platform: TelemetryPlatform
) {
    private val _consent = MutableStateFlow(runCatching { store.load() }.getOrDefault(TelemetryConsent()))
    val consent: StateFlow<TelemetryConsent> = _consent.asStateFlow()

    init {
        apply(_consent.value)
    }

    /** Whether to show the first-run question: never asked, and this build can collect at all. */
    val shouldAsk: Boolean get() = platform.canCollect && !_consent.value.asked

    /** The first-run answer: "Allow selected" with what was ticked, or "Not now" (both false). */
    fun answer(crashReports: Boolean, usageStats: Boolean) =
        update(TelemetryConsent(crashReports = crashReports && platform.supportsCrashReports, usageStats = usageStats, asked = true))

    fun setCrashReports(enabled: Boolean) =
        update(_consent.value.copy(crashReports = enabled && platform.supportsCrashReports, asked = true))

    fun setUsageStats(enabled: Boolean) = update(_consent.value.copy(usageStats = enabled, asked = true))

    private fun update(consent: TelemetryConsent) {
        _consent.value = consent
        runCatching { store.save(consent) }
        apply(consent)
    }

    private fun apply(consent: TelemetryConsent) =
        telemetry.setCollection(analytics = consent.usageStats, crashes = consent.crashReports)
}
